<template>
  <AuthShell width="wide">
    <div v-if="registrationCompleted" class="auth-form">
      <h1 class="auth-form__title">Account registered</h1>
      <p class="auth-form__sub">Your account has been created. Sign in to get started.</p>
      <v-btn color="primary" size="large" class="align-self-start" @click="redirectionToLoginPage">
        Continue to sign in
      </v-btn>
    </div>

    <v-form v-else class="auth-form" @submit.prevent="registerUser">
      <h1 class="auth-form__title">Complete your registration</h1>
      <p class="auth-form__sub">Tell us about your practice so we can submit prior authorizations on your behalf.</p>

      <div class="auth-form__grid">
        <div class="auth-form__field">
          <label for="first-name" class="auth-form__label">First name</label>
          <v-text-field
            id="first-name"
            v-model="userInfo.first_name"
            placeholder="John"
            :error-messages="
              !isFieldNotEmpty('first_name') && formButtonClicked ? 'Please enter your first name' : ''
            " />
        </div>

        <div class="auth-form__field">
          <label for="last-name" class="auth-form__label">Last name</label>
          <v-text-field
            id="last-name"
            v-model="userInfo.last_name"
            placeholder="Snow"
            :error-messages="!isFieldNotEmpty('last_name') && formButtonClicked ? 'Please enter your last name' : ''" />
        </div>

        <div class="auth-form__field">
          <label for="phone-number" class="auth-form__label">Phone number</label>
          <v-text-field
            id="phone-number"
            v-model="userInfo.phone"
            type="tel"
            placeholder="1-123-456-7890"
            :error-messages="!isPhoneNumberValid && formButtonClicked ? 'Please enter a valid phone number' : ''" />
        </div>

        <div class="auth-form__field">
          <label for="phone-number-extension" class="auth-form__label">Phone extension</label>
          <v-text-field id="phone-number-extension" v-model="userInfo.phone_extension" placeholder="123" />
        </div>

        <div class="auth-form__field">
          <label for="fax" class="auth-form__label">Fax</label>
          <v-text-field
            id="fax"
            v-model="userInfo.fax"
            placeholder="1-123-456-7890"
            :error-messages="!isFaxValid && formButtonClicked ? 'Please enter a valid fax number' : ''" />
        </div>

        <div class="auth-form__field">
          <label for="tin" class="auth-form__label">TIN</label>
          <v-text-field
            id="tin"
            v-model="userInfo.tin"
            placeholder="123456789"
            :error-messages="!isTinValid && formButtonClicked ? 'Please enter a valid 9 digit TIN' : ''" />
        </div>

        <div class="auth-form__field">
          <label for="npi" class="auth-form__label">NPI</label>
          <v-text-field
            id="npi"
            v-model="userInfo.npi"
            placeholder="1234567890"
            :error-messages="!isNpiValid && formButtonClicked ? 'Please enter a valid 10 digit NPI' : ''" />
        </div>

        <div class="auth-form__field auth-form__grid--full">
          <label for="practice-title" class="auth-form__label">Practice title</label>
          <v-text-field
            id="practice-title"
            v-model="userInfo.practice_title"
            :error-messages="
              !isFieldNotEmpty('practice_title') && formButtonClicked
                ? 'Please enter the practice you submit on behalf of'
                : ''
            " />
        </div>

        <div class="auth-form__field">
          <label for="specialty" class="auth-form__label">Specialty</label>
          <v-text-field
            id="specialty"
            v-model="userInfo.specialty"
            :error-messages="!isFieldNotEmpty('specialty') && formButtonClicked ? 'Please enter your specialty' : ''" />
        </div>

        <div class="auth-form__field">
          <label for="license-or-certification" class="auth-form__label">License or certification</label>
          <v-text-field
            id="license-or-certification"
            v-model="userInfo.license_or_certification"
            :error-messages="
              !isFieldNotEmpty('license_or_certification') && formButtonClicked
                ? 'Please enter a license or certification'
                : ''
            " />
        </div>

        <template v-if="insurances">
          <div class="auth-form__field">
            <label class="auth-form__label">States covered</label>
            <CheckboxMultipleSelectComponent
              :options="states"
              :selected-options="userInfo.states_served"
              placeholder="Select states covered"
              @selection-change="(event) => handleSelectionChange(event, 'states_served')" />
            <span v-if="!isArrayFieldNotEmpty('states_served') && formButtonClicked" class="auth-form__error">
              Please select at least 1 state.
            </span>
          </div>

          <div class="auth-form__field">
            <label class="auth-form__label">Insurance providers accepted</label>
            <CheckboxMultipleSelectComponent
              :options="insurances"
              :selected-options="userInfo.insurances_accepted"
              placeholder="Select accepted insurance providers"
              :allow-free-text-input="true"
              @selection-change="(event) => handleSelectionChange(event, 'insurances_accepted')" />
            <span v-if="!isArrayFieldNotEmpty('insurances_accepted') && formButtonClicked" class="auth-form__error">
              Please select at least 1 insurance provider.
            </span>
          </div>
        </template>

        <div class="auth-form__field">
          <label for="password" class="auth-form__label">Password</label>
          <v-text-field
            id="password"
            v-model="userInfo.password"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="new-password"
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            :error-messages="
              !isPasswordValid && formButtonClicked
                ? 'At least 11 characters with an upper and lower case letter, a number and a symbol'
                : ''
            "
            @click:append-inner="showPassword = !showPassword" />
        </div>

        <div class="auth-form__field">
          <label for="confirm-password" class="auth-form__label">Confirm password</label>
          <v-text-field
            id="confirm-password"
            v-model="passwordConfirmation"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="new-password"
            :error-messages="!isPasswordMatchValid && formButtonClicked ? 'Passwords must match' : ''" />
        </div>
      </div>

      <div>
        <v-checkbox v-model="acceptTermsOfService" density="compact">
          <template #label>
            <span class="auth-form__terms">
              I accept the
              <a href="https://www.dopriorauth.com/company/tos" target="_blank" class="auth-form__link">
                Terms of Service and Business Associate Agreement
              </a>
            </span>
          </template>
        </v-checkbox>
        <span v-if="!acceptTermsOfService && formButtonClicked" class="auth-form__error">
          Must accept terms of service before continuing.
        </span>
        <p class="auth-form__notice">
          The information you provide is used during account set-up validation and is subject to our
          <a href="https://www.dopriorauth.com/company/tos" target="_blank" class="auth-form__link">
            Terms of Service and Business Associate Agreement </a
          >.
        </p>
      </div>

      <v-btn color="primary" size="large" type="submit" class="align-self-start" :loading="showPreloader">
        Complete registration
      </v-btn>

      <v-alert v-if="errors.length > 0" type="error" variant="tonal">
        <div v-for="error in errors" :key="error">{{ error }}</div>
      </v-alert>

      <p class="auth-form__center">
        Already have an account?
        <router-link :to="{ name: 'login' }" class="auth-form__link">Sign in</router-link>
      </p>
    </v-form>
  </AuthShell>
</template>

<script setup>
import { ref, computed, onBeforeMount, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import CheckboxMultipleSelectComponent from "@/components/CheckboxMultipleSelectComponent";
import authService from "@/services/authService";
import { mainServices } from "@/services/mainServices";
import { tryParseApiErrors } from "@/utils";
import { FORM_VALIDATION_PATTERNS } from "@/utils/constants";
import { usaStates } from "@/utils/usaStates";
import AuthShell from "@/components/AuthShell.vue";

const route = useRoute();
const router = useRouter();

const userInfo = ref({
  first_name: "",
  last_name: "",
  password: "",
  phone: "",
  phone_extension: "",
  fax: "",
  specialty: "",
  tin: "",
  npi: "",
  practice_title: "",
  license_or_certification: "",
  states_served: [],
  insurances_accepted: [],
});

const states = ref([]);
const insurances = ref(null);
const formButtonClicked = ref(false);
const errors = ref([]);
const showPreloader = ref(false);
const showPassword = ref(false);
const passwordConfirmation = ref(null);
const acceptTermsOfService = ref(false);
const registrationCompleted = ref(false);

onBeforeMount(async () => {
  for (let stateData of Object.values(usaStates)) {
    states.value.push(stateData.name);
  }
  insurances.value = await mainServices.getInsuranceProviders();
});

/** Inserts the dashes as the user types a phone or fax number. */
function attachDashFormatter(selector) {
  const input = document.querySelector(selector);
  if (!input) return;
  input.addEventListener("keypress", (event) => {
    const length = event.target.value.length;
    if (length === 1 || length === 5 || length === 9) {
      event.target.value = event.target.value + "-";
    }
  });
}

onMounted(() => {
  attachDashFormatter("#phone-number");
  attachDashFormatter("#fax");
});

// Validators
function isFieldNotEmpty(fieldValue) {
  if (!fieldValue) return false;
  const value = userInfo.value[fieldValue];
  return value !== null && value.trim() !== "";
}

function isArrayFieldNotEmpty(fieldValue) {
  if (!fieldValue) return false;
  return userInfo.value[fieldValue].length > 0;
}

const isPasswordValid = computed(() => {
  const password = userInfo.value.password;
  return FORM_VALIDATION_PATTERNS.PASSWORD_PATTERN.test(password);
});

const isPasswordMatchValid = computed(() => {
  return userInfo.value.password === passwordConfirmation.value;
});

const isPhoneNumberValid = computed(() => {
  const phoneNumber = userInfo.value.phone;
  return phoneNumber !== "" && FORM_VALIDATION_PATTERNS.PHONE_NUMBER_PATTERN.test(phoneNumber);
});

const isFaxValid = computed(() => {
  const fax = userInfo.value.fax;
  return fax !== "" && FORM_VALIDATION_PATTERNS.PHONE_NUMBER_PATTERN.test(fax);
});

const isTinValid = computed(() => {
  const tin = userInfo.value.tin;
  return tin !== "" && FORM_VALIDATION_PATTERNS.TIN_PATTERN.test(tin);
});

const isNpiValid = computed(() => {
  const npi = userInfo.value.npi;
  return npi !== "" && FORM_VALIDATION_PATTERNS.NPI_PATTERN.test(npi);
});

async function registerUser() {
  formButtonClicked.value = true;

  if (
    isFieldNotEmpty("first_name") &&
    isFieldNotEmpty("last_name") &&
    isFieldNotEmpty("specialty") &&
    isFieldNotEmpty("practice_title") &&
    isFieldNotEmpty("license_or_certification") &&
    isArrayFieldNotEmpty("states_served") &&
    isArrayFieldNotEmpty("insurances_accepted") &&
    isTinValid.value &&
    isNpiValid.value &&
    isPhoneNumberValid.value &&
    isFaxValid.value &&
    isPasswordValid.value &&
    isPasswordMatchValid.value &&
    acceptTermsOfService.value
  ) {
    try {
      showPreloader.value = true;
      await authService.completeRegistration(route.params.user_id, route.params.token, userInfo.value);
      registrationCompleted.value = true;
      errors.value = [];
    } catch (error) {
      errors.value = tryParseApiErrors(error);
    }
  } else {
    setTimeout(() => {
      const missingInput = document.getElementsByClassName("v-input--error");
      if (missingInput.length > 0) {
        missingInput[0].scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 0);
  }
  showPreloader.value = false;
}

function handleSelectionChange(selectedOptions, filterOptions) {
  userInfo.value[filterOptions] = selectedOptions;
}

function redirectionToLoginPage() {
  router.push({ name: "login" });
}
</script>

<style lang="scss" scoped>
@import "@/styles/pages/_auth-form.scss";

.auth-form__error {
  font-size: 12px;
  color: $lh_color_red;
}

.auth-form__terms {
  font-size: 14px;
  color: $lh_color_navy_dark;
}

.auth-form__notice {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: $lh_color_blue_gray;
}
</style>
